import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Calendar,
  MapPin,
  Users,
  DollarSign,
  Clock,
  CheckSquare,
  Camera,
  ClipboardList,
  Grid3X3,
  ChevronRight,
  Printer,
  Mail,
  MessageSquare,
  Armchair,
} from 'lucide-react';
import { formatTimeDisplay } from '@peles/shared';
import type { ExpenseSummary } from '@peles/shared';
import { ClientFloorPlanCanvas } from '../components/floor-plan/ClientFloorPlanCanvas';
import { ClientPlanPart } from '../components/portal/ClientPlanPart';
import { ClientTaskList } from '../components/portal/ClientTaskList';
import { GuestListView } from '../components/guests/list/GuestListView';
import { SeatingBoard } from '../components/seating/SeatingBoard';
import {
  useClientEventDetail,
  useClientExpenseSummary,
  useClientExpenseItems,
  useClientEventFloorPlans,
  useClientEventPhotos,
  useClientEventTimeline,
  useClientEventQuestionnaires,
} from '../hooks/usePortal';
import { ClientExpenseDocumentsModal } from '../components/portal/ClientExpenseDocumentsModal';
import { ClientExpenseTable } from '../components/portal/ClientExpenseTable';
import { ClientExpensePaymentModal } from '../components/portal/ClientExpensePaymentModal';
import type { ClientExpenseItem } from '../api/portal';
import { QuestionnaireFillView } from '../components/questionnaires';
import { InviteRsvpSection } from '../components/invites/InviteRsvpSection';
import { CampaignWorkspace } from '../components/invites/campaign/CampaignWorkspace';
import { ClientEventOverview } from '../components/portal/ClientEventOverview';
import { PortalConversations } from '../components/portal/PortalConversations';
import { EventHeader, MetaChip } from '../components/events/EventHeader';
import { EventLogo } from '../components/events/EventLogo';
import { EventStatusBadge } from '../components/events/EventStatusBadge';
import { TabNav } from '../components/common/TabNav';
import { useViewMode } from '../context/ViewModeContext';
import { useDialog } from '../context/DialogContext';
import { formatEventDateTime } from '../utils/eventDates';
import { printDocument } from '../utils/printDocument';

type Tab =
  | 'overview'
  | 'expenses'
  | 'guests'
  | 'floor-plan'
  | 'seating'
  | 'timeline'
  | 'tasks'
  | 'photos'
  | 'questionnaires'
  | 'invites'
  | 'conversations';

const TABS: { id: Tab; label: string; icon: typeof Calendar }[] = [
  { id: 'overview', label: 'Overview', icon: Calendar },
  { id: 'expenses', label: 'Expenses', icon: DollarSign },
  { id: 'guests', label: 'Guests', icon: Users },
  { id: 'floor-plan', label: 'Floor Plan', icon: Grid3X3 },
  // Everyone reads who sits where; canManageSeating lets the client change it.
  { id: 'seating', label: 'Seating', icon: Armchair },
  { id: 'timeline', label: 'Timeline', icon: Clock },
  // Every client: the tasks assigned to them. Adding their own needs canManageTasks.
  { id: 'tasks', label: 'Tasks', icon: CheckSquare },
  { id: 'photos', label: 'Photos', icon: Camera },
  { id: 'questionnaires', label: 'Questionnaires', icon: ClipboardList },
  // Only shown when the client's permissions include canManageInvites.
  { id: 'invites', label: 'Invites', icon: Mail },
  // Only shown when the office has turned conversations on for this client (canMessage).
  { id: 'conversations', label: 'Conversations', icon: MessageSquare },
];

/** The client's budget tiles — 'Owed to Producer' only when money was fronted. */
function summaryTiles(summary: ExpenseSummary): {
  label: string;
  value: string;
  className?: string;
}[] {
  const tiles = [
    { label: 'Total', value: `$${summary.totalCost.toLocaleString()}` },
    { label: 'Paid', value: `$${summary.totalPaid.toLocaleString()}`, className: 'text-green-600' },
    {
      label: 'Open Balance',
      value: `$${summary.balance.toLocaleString()}`,
      className: 'text-red-600',
    },
  ];
  if (summary.producerReimbursementDue > 0.005) {
    tiles.push({
      label: 'Owed to Producer',
      value: `$${summary.producerReimbursementDue.toLocaleString()}`,
      className: 'text-amber-600',
    });
  }
  tiles.push({ label: 'Items', value: String(summary.itemCount) });
  return tiles;
}

function ExpensesTab({ eventId, eventName }: { eventId: string; eventName?: string }) {
  const { data: summary, isLoading: summaryLoading } = useClientExpenseSummary(eventId);
  const { data: items, isLoading: itemsLoading } = useClientExpenseItems(eventId);
  const [docPreviewId, setDocPreviewId] = useState<string | null>(null);
  const [payFor, setPayFor] = useState<ClientExpenseItem | null>(null);

  if (summaryLoading || itemsLoading) return <p className="text-gray-500 p-4">Loading...</p>;

  return (
    <div className="space-y-4">
      {docPreviewId && (
        <ClientExpenseDocumentsModal
          eventId={eventId}
          expenseItemId={docPreviewId}
          onClose={() => setDocPreviewId(null)}
        />
      )}

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {summaryTiles(summary).map(({ label, value, className }) => (
            <div key={label} className="bg-white rounded-lg border p-3">
              <p className="text-xs text-gray-500">{label}</p>
              <p className={`text-xl font-bold ${className ?? ''}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      <ClientExpenseTable
        items={items ?? []}
        onPreviewDocuments={(item) => setDocPreviewId(item.id)}
        onPay={setPayFor}
      />
      <ClientExpensePaymentModal
        eventId={eventId}
        eventName={eventName}
        item={payFor}
        onClose={() => setPayFor(null)}
      />
    </div>
  );
}

function FloorPlanTab({ eventId, eventName }: { eventId: string; eventName?: string }) {
  const { data: plans, isLoading } = useClientEventFloorPlans(eventId);
  const { data: event } = useClientEventDetail(eventId);
  const canPrint = event?.permissions?.canPrint ?? false;
  const [openPlan, setOpenPlan] = useState<string | null>(null);

  if (isLoading) return <p className="text-gray-500 p-4">Loading...</p>;
  if (!plans || plans.length === 0)
    return <p className="text-gray-500 text-center py-8">No floor plans available.</p>;

  return (
    <div className="space-y-4">
      {plans.map((plan) => {
        const tables = plan.objects.filter((o) => o.tableNumber != null);
        const objects = plan.objects.filter((o) => o.tableNumber == null);
        const isOpen = openPlan === plan.id;

        return (
          <div key={plan.id} className="bg-white rounded-lg border overflow-hidden">
            <button
              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
              onClick={() => setOpenPlan(isOpen ? null : plan.id)}
            >
              <div>
                <h3 className="font-semibold">{plan.name}</h3>
                <ClientPlanPart plan={plan} />
                {plan.roomName && <p className="text-sm text-gray-500">{plan.roomName}</p>}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">
                  {tables.length} tables · {objects.length} objects
                </span>
                <ChevronRight
                  size={18}
                  className={`transition-transform ${isOpen ? 'rotate-90' : ''}`}
                />
              </div>
            </button>

            {isOpen && (
              <div className="border-t p-4">
                <ClientFloorPlanCanvas
                  plan={plan}
                  eventName={eventName}
                  eventDate={event?.startAt ?? null}
                  venueName={event?.venueName ?? null}
                  canPrint={canPrint}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TimelineTab({ eventId, eventName }: { eventId: string; eventName?: string }) {
  const { data: timelines, isLoading } = useClientEventTimeline(eventId);
  const { data: event } = useClientEventDetail(eventId);
  const canPrint = event?.permissions?.canPrint ?? false;

  // Entries arrive from the portal API with `{tag}` placeholders already
  // merged to the event's tag values — print them as displayed.
  const handlePrint = (timeline: NonNullable<typeof timelines>[number]) => {
    const esc = (s: string | null | undefined) =>
      (s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const title = eventName ? `${eventName}: ${timeline.name}` : timeline.name;
    const sectionsHtml = timeline.sections
      .map(
        (section) =>
          `<h2>${esc(section.name)}</h2><table>` +
          section.entries
            .map(
              (entry) =>
                `<tr><td class="t">${esc(formatTimeDisplay(entry.time))}</td><td><strong>${esc(entry.title)}</strong>` +
                (entry.location ? `<div class="loc">${esc(entry.location)}</div>` : '') +
                (entry.notes ? `<div class="notes">${esc(entry.notes)}</div>` : '') +
                `</td></tr>`,
            )
            .join('') +
          `</table>`,
      )
      .join('');
    printDocument(
      `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${esc(title)}</title>` +
        `<style>` +
        `@page{margin:15mm;}` +
        `body{margin:0;font-family:sans-serif;color:#111;font-size:13px;}` +
        `h1{font-size:18px;margin:0 0 2px 0;}` +
        `.desc{color:#555;font-size:12px;margin:0 0 12px 0;}` +
        `h2{font-size:12px;text-transform:uppercase;letter-spacing:0.05em;color:#555;border-bottom:1px solid #ddd;padding-bottom:4px;margin:16px 0 6px 0;}` +
        `table{width:100%;border-collapse:collapse;}` +
        `td{padding:6px 8px;border-bottom:1px solid #f0f0f0;vertical-align:top;}` +
        `td.t{width:70px;font-family:monospace;color:#555;white-space:nowrap;}` +
        `.loc{color:#555;font-size:12px;}` +
        `.notes{color:#444;font-size:12px;margin-top:2px;}` +
        `</style></head><body>` +
        `<h1>${esc(title)}</h1>` +
        (timeline.description ? `<p class="desc">${esc(timeline.description)}</p>` : '') +
        sectionsHtml +
        `</body></html>`,
    );
  };

  if (isLoading) return <p className="text-gray-500 p-4">Loading...</p>;
  if (!timelines || timelines.length === 0)
    return <p className="text-gray-500 text-center py-8">No timeline available.</p>;

  return (
    <div className="space-y-4">
      {timelines.map((timeline) => (
        <div key={timeline.id} className="bg-white rounded-lg border overflow-hidden">
          <div className="p-4 border-b bg-gray-50 flex items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold">{timeline.name}</h3>
              {timeline.description && (
                <p className="text-sm text-gray-500 mt-1">{timeline.description}</p>
              )}
            </div>
            {canPrint && (
              <button
                onClick={() => handlePrint(timeline)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm text-gray-700 shrink-0"
                data-testid={`print-timeline-${timeline.id}`}
              >
                <Printer size={15} />
                Print
              </button>
            )}
          </div>
          {timeline.sections.map((section) => (
            <div key={section.id}>
              <div className="px-4 py-2 bg-gray-100 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                {section.name}
              </div>
              <div className="divide-y">
                {section.entries.map((entry) => (
                  <div key={entry.id} className="p-4 flex gap-4">
                    {entry.time && (
                      <div className="text-sm font-mono text-gray-500 shrink-0 w-16">
                        {formatTimeDisplay(entry.time)}
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium">{entry.title}</p>
                      {entry.location && (
                        <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                          <MapPin size={12} />
                          {entry.location}
                        </p>
                      )}
                      {entry.notes && <p className="text-sm text-gray-600 mt-1">{entry.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function PhotosTab({ eventId }: { eventId: string }) {
  const { data: photos, isLoading } = useClientEventPhotos(eventId);

  if (isLoading) return <p className="text-gray-500 p-4">Loading...</p>;
  if (!photos || photos.length === 0)
    return <p className="text-gray-500 text-center py-8">No photos available.</p>;

  return (
    <div>
      <p className="text-sm text-gray-500 mb-4">{photos.length} photo(s) attached to this event.</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {photos.map((photo) => (
          <div key={photo.id} className="bg-white rounded-lg border overflow-hidden">
            <div className="aspect-square bg-gray-100 flex items-center justify-center">
              <Camera size={32} className="text-gray-400" />
            </div>
            <div className="p-2">
              <p className="text-xs text-gray-600 truncate">{photo.fileName}</p>
              {photo.caption && <p className="text-xs text-gray-500 truncate">{photo.caption}</p>}
              {photo.takenAt && (
                <p className="text-xs text-gray-400">
                  {new Date(photo.takenAt).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const QUESTIONNAIRE_STATUS_COLORS: Record<string, string> = {
  SENT: 'bg-blue-100 text-blue-700',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
  SUBMITTED: 'bg-green-100 text-green-700',
  REVIEWED: 'bg-purple-100 text-purple-700',
};

const QUESTIONNAIRE_STATUS_LABELS: Record<string, string> = {
  SENT: 'Not Started',
  IN_PROGRESS: 'In Progress',
  SUBMITTED: 'Submitted',
  REVIEWED: 'Reviewed',
};

function QuestionnairesTab({
  eventId,
  initialQuestionnaireId,
}: {
  eventId: string;
  initialQuestionnaireId?: string | null;
}) {
  const { data: questionnaires, isLoading } = useClientEventQuestionnaires(eventId);
  const [openId, setOpenId] = useState<string | null>(initialQuestionnaireId ?? null);

  // A single questionnaire opens directly — no list to pick from.
  useEffect(() => {
    if (questionnaires?.length === 1) setOpenId(questionnaires[0].id);
  }, [questionnaires]);

  if (isLoading) return <p className="text-gray-500 p-4">Loading...</p>;
  if (!questionnaires || questionnaires.length === 0)
    return <p className="text-gray-500 text-center py-8">No questionnaires available.</p>;

  return (
    <div className="space-y-4">
      {questionnaires.map((questionnaire) => {
        const isOpen = openId === questionnaire.id;
        return (
          <div key={questionnaire.id} className="bg-white rounded-lg border overflow-hidden">
            <button
              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
              onClick={() => setOpenId(isOpen ? null : questionnaire.id)}
              data-testid={`questionnaire-row-${questionnaire.id}`}
            >
              <div className="min-w-0">
                <h3 className="font-semibold truncate">{questionnaire.title}</h3>
                {questionnaire.description && (
                  <p className="text-sm text-gray-500 truncate">{questionnaire.description}</p>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm text-gray-500 hidden sm:inline">
                  {questionnaire.answeredCount}/{questionnaire.questionCount} answered
                </span>
                <span
                  className={`text-xs px-2 py-0.5 rounded font-medium ${
                    QUESTIONNAIRE_STATUS_COLORS[questionnaire.status] || 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {QUESTIONNAIRE_STATUS_LABELS[questionnaire.status] || questionnaire.status}
                </span>
                <ChevronRight
                  size={18}
                  className={`transition-transform ${isOpen ? 'rotate-90' : ''}`}
                />
              </div>
            </button>

            {isOpen && (
              <div className="border-t p-4 bg-gray-50">
                <QuestionnaireFillView token={questionnaire.accessToken} variant="embedded" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function ClientEventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile } = useViewMode();
  // The tab lives in the address (?tab=…), so a deep link — the public
  // questionnaire redirect, say — opens the right one, and reload keeps it.
  // `?tab=checklist` is the tab's old address — it opens the client's tasks.
  const tabParam = searchParams.get('tab') === 'checklist' ? 'tasks' : searchParams.get('tab');
  const { confirm } = useDialog();
  const campaignDirty = useRef(false);
  const setActiveTab = async (tab: Tab) => {
    // Leaving the Invites tab closes an open campaign; one with unsaved
    // changes asks first, as its own back button does.
    if (campaignDirty.current) {
      const ok = await confirm({
        message: 'Leave this step without saving your changes?',
        variant: 'warning',
        confirmLabel: 'Discard changes',
      });
      if (!ok) return;
      campaignDirty.current = false;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 'overview') next.delete('tab');
        else next.set('tab', tab);
        // A campaign open on the Invites tab is closed by leaving it.
        next.delete('campaign');
        next.delete('step');
        return next;
      },
      { replace: true },
    );
  };
  const { data: event, isLoading } = useClientEventDetail(eventId!);

  if (isLoading) return <p className="p-4 text-gray-500">Loading event...</p>;
  if (!event) return <p className="p-4 text-gray-500">Event not found.</p>;

  const canManageInvites = event.permissions?.canManageInvites ?? false;
  const canManageTasks = event.permissions?.canManageTasks ?? false;
  const canMessage = event.permissions?.canMessage ?? false;
  const visibleTabs = TABS.filter(
    (tab) =>
      (tab.id !== 'invites' || canManageInvites) && (tab.id !== 'conversations' || canMessage),
  );
  // A tab this client cannot see (an old link, a permission since withdrawn)
  // opens the overview rather than an empty page.
  const activeTab: Tab = visibleTabs.some((t) => t.id === tabParam)
    ? (tabParam as Tab)
    : 'overview';

  return (
    <div>
      <EventHeader
        compact={isMobile}
        backTo={{ label: 'My events', to: '/client' }}
        title={event.name}
        startAt={event.startAt}
        logo={
          <EventLogo
            eventId={event.id}
            eventLogoUrl={event.logoUrl ?? null}
            sizeClassName={isMobile ? 'w-12 h-12' : 'w-16 h-16'}
          />
        }
        meta={
          <>
            <EventStatusBadge status={event.status} />
            <MetaChip icon={<Calendar size={13} className="text-gray-400" />}>
              {formatEventDateTime(event.startAt, event.endAt)}
            </MetaChip>
            {event.venueName && (
              <MetaChip icon={<MapPin size={13} className="text-gray-400" />}>
                {event.venueName}
              </MetaChip>
            )}
            {event.eventTypeName && (
              <span className="px-2.5 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold">
                {event.eventTypeName}
              </span>
            )}
          </>
        }
        tabs={
          <TabNav
            ariaLabel="Event sections"
            items={visibleTabs.map((t) => ({
              id: t.id,
              label: t.label,
              icon: <t.icon size={15} />,
            }))}
            activeId={activeTab}
            onSelect={(id) => void setActiveTab(id as Tab)}
          />
        }
      />

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && (
          <ClientEventOverview eventId={eventId!} onOpenTab={(tab) => void setActiveTab(tab)} />
        )}
        {activeTab === 'expenses' && <ExpensesTab eventId={eventId!} eventName={event.name} />}
        {activeTab === 'guests' && <GuestListView eventId={eventId!} />}
        {activeTab === 'floor-plan' && <FloorPlanTab eventId={eventId!} eventName={event.name} />}
        {activeTab === 'seating' && (
          <SeatingBoard
            eventId={eventId!}
            canWrite={event.permissions?.canManageSeating ?? false}
            office={false}
          />
        )}
        {activeTab === 'timeline' && <TimelineTab eventId={eventId!} eventName={event.name} />}
        {activeTab === 'tasks' && (
          <ClientTaskList
            eventId={eventId!}
            eventStartAt={event.startAt}
            cancelled={event.status === 'CANCELLED'}
            canAdd={canManageTasks}
          />
        )}
        {activeTab === 'photos' && <PhotosTab eventId={eventId!} />}
        {activeTab === 'questionnaires' && (
          <QuestionnairesTab
            eventId={eventId!}
            initialQuestionnaireId={searchParams.get('questionnaire')}
          />
        )}
        {activeTab === 'invites' &&
          (searchParams.get('campaign') ? (
            <CampaignWorkspace
              key={searchParams.get('campaign')}
              eventId={eventId!}
              campaignId={searchParams.get('campaign')!}
              clientMode
              event={{
                name: event.name,
                startAt: event.startAt,
                endAt: event.endAt,
                venueName: event.venueName,
                venueAddress: event.venueAddress,
                logoUrl: event.logoUrl,
              }}
              step={searchParams.get('step')}
              onDirtyChange={(dirty) => {
                campaignDirty.current = dirty;
              }}
              onStepChange={(step) =>
                setSearchParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('step', step);
                    return next;
                  },
                  { replace: true },
                )
              }
              onBack={() =>
                setSearchParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete('campaign');
                    next.delete('step');
                    return next;
                  },
                  { replace: true },
                )
              }
            />
          ) : (
            <InviteRsvpSection eventId={eventId!} clientMode />
          ))}
        {activeTab === 'conversations' && (
          <PortalConversations key={eventId} entityType="Event" entityId={eventId!} />
        )}
      </div>
    </div>
  );
}
